import React from "react";

// region Languages
import useTranslation from "@languages/useTranslation";
import { AnnouncementMessages } from "@languages/interfaces";
// endregion Languages
// region Libraries
import { Notifications } from "@libraries/mui/icons";
// endregion Libraries
// region Molecules
import NotificationItem from "@molecules/NotificationItem";
// endregion Molecules
// region Store
import { MyAnnouncement } from "src/store/graphql/groups/announcement/query.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

// region Constants
// The dropdown shows only the most recent items; the full list lives in the
// "view all" dialog.
const VISIBLE_ITEMS = 5;
// endregion Constants

// region Interfaces
interface NotificationMenuProps {
  anchorElement: null | HTMLElement;
  announcements: MyAnnouncement[];
  unreadCount: number;
  onClose: () => void;
  onItemRead: (id: string) => void;
  onItemExposed: (id: string) => void;
  onCtaClick: (item: MyAnnouncement) => void;
  onMarkAllRead: () => void;
  onViewAll: () => void;
}
// endregion Interfaces

/**
 * Bell dropdown: the most recent announcements with read state, "mark all as
 * read" and the "view all" footer. Anchoring follows the LanguageMenu pattern.
 */
const NotificationMenu: React.FC<NotificationMenuProps> = ({
  anchorElement,
  announcements,
  unreadCount,
  onClose,
  onItemRead,
  onItemExposed,
  onCtaClick,
  onMarkAllRead,
  onViewAll
}) => {

  const { t } = useTranslation();

  const visibleItems = announcements.slice(0, VISIBLE_ITEMS);

  return (
    <Styled.Container
      sx={{ mt: "45px" }}
      anchorEl={anchorElement}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(anchorElement)}
      onClose={onClose}
    >
      <Styled.Header>
        <Styled.HeaderTitle>{t(AnnouncementMessages.dropdownTitle)}</Styled.HeaderTitle>
        {unreadCount > 0 && (
          <Styled.HeaderAction type="button" onClick={onMarkAllRead}>
            {t(AnnouncementMessages.markAllRead)}
          </Styled.HeaderAction>
        )}
      </Styled.Header>

      {visibleItems.length === 0 && (
        <Styled.EmptyState>
          <Notifications />
          <Styled.EmptyTitle>{t(AnnouncementMessages.emptyTitle)}</Styled.EmptyTitle>
          <Styled.EmptySubtitle>{t(AnnouncementMessages.emptySubtitle)}</Styled.EmptySubtitle>
        </Styled.EmptyState>
      )}

      {visibleItems.map((item) => (
        <NotificationItem
          key={item.id}
          item={item}
          onRead={onItemRead}
          onExposed={onItemExposed}
          onCtaClick={onCtaClick}
        />
      ))}

      {announcements.length > 0 && (
        <Styled.Footer type="button" onClick={onViewAll}>
          {t(AnnouncementMessages.viewAll)}
        </Styled.Footer>
      )}
    </Styled.Container>
  );
};

export default NotificationMenu;
